import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Filter, 
  Calendar, 
  MapPin, 
  Building2, 
  Wrench,
  Eye,
  Edit,
  Trash2,
  FileText,
  RefreshCw
} from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import { toast } from 'sonner';

const CaseStudiesList = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deleteLoading, setDeleteLoading] = useState(null);

  const loadCaseStudies = useCallback(async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/case-studies`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load case studies');
      }

      const data = await response.json();
      // Filter out invalid blob URLs from each case study
      const cleanedData = data.map(study => ({
        ...study,
        beforeImages: (study.beforeImages || []).filter(img => img && img.startsWith('http')),
        afterImages: (study.afterImages || []).filter(img => img && img.startsWith('http'))
      }));
      setCaseStudies(cleanedData);
    } catch (error) {
      console.error('Error loading case studies:', error);
      toast.error('Failed to load case studies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
        });

        if (!response.ok) {
          navigate('/');
          return;
        }

        setIsAuthenticated(true);
        loadCaseStudies();
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/');
      }
    };

    checkAuth();
  }, [navigate, loadCaseStudies]);

  const handleDelete = async (caseId) => {
    if (!window.confirm('Are you sure you want to delete this case study?')) {
      return;
    }

    setDeleteLoading(caseId);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/case-studies/${caseId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete case study');
      }

      toast.success('Case study deleted');
      loadCaseStudies();
    } catch (error) {
      console.error('Error deleting case study:', error);
      toast.error('Failed to delete case study');
    } finally {
      setDeleteLoading(null);
    }
  };

  const filteredStudies = caseStudies.filter(study => {
    const matchesSearch = (study.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (study.problemDescription || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesIndustry = filterIndustry === 'all' || study.industry === filterIndustry;
    const matchesStatus = filterStatus === 'all' || study.status === filterStatus;
    return matchesSearch && matchesIndustry && matchesStatus;
  });

  const industries = [...new Set(caseStudies.map(s => s.industry).filter(i => i && i.trim() !== ''))];

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Page Header */}
      <div className="pt-32 pb-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/customer-portal')}
            className="mb-4 text-gray-600 hover:text-gray-900"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">
                Case Studies
              </h1>
              <p className="text-xl text-gray-600">
                Document and showcase your repair application projects
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={loadCaseStudies}
                disabled={loading}
                data-testid="refresh-case-studies-btn"
              >
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              </Button>
              <Button
                onClick={() => navigate('/customer-portal/case-studies/create')}
                className="bg-blue-900 hover:bg-blue-800 flex items-center gap-2"
                data-testid="create-case-study-btn"
              >
                <Plus size={18} />
                Create Case Study
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="py-6 px-4 sm:px-6 lg:px-8 border-b bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                placeholder="Search case studies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-case-studies-input"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filterIndustry} onValueChange={setFilterIndustry}>
                <SelectTrigger className="w-[200px]" data-testid="filter-industry-select">
                  <Filter size={16} className="mr-2" />
                  <SelectValue placeholder="Industry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Industries</SelectItem>
                  {industries.map((industry) => (
                    <SelectItem key={industry} value={industry}>
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px]" data-testid="filter-status-select">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Published">Published</SelectItem>
                  <SelectItem value="Draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Case Studies Grid */}
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading case studies...</p>
            </div>
          ) : filteredStudies.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <FileText className="mx-auto text-gray-400 mb-4" size={48} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Case Studies Found</h3>
                <p className="text-gray-600 mb-4">
                  {searchTerm || filterIndustry !== 'all' || filterStatus !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Get started by creating your first case study'}
                </p>
                <Button
                  onClick={() => navigate('/customer-portal/case-studies/create')}
                  className="bg-blue-900 hover:bg-blue-800"
                >
                  <Plus size={18} className="mr-2" />
                  Create Case Study
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStudies.map((study) => (
                <Card key={study.case_id} className="hover:shadow-lg transition-shadow" data-testid={`case-study-card-${study.case_id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <Badge 
                        className={study.status === 'Published' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'}
                      >
                        {study.status || 'Published'}
                      </Badge>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/customer-portal/case-studies/${study.case_id}`)}
                          data-testid={`view-case-study-${study.case_id}`}
                        >
                          <Eye size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/customer-portal/case-studies/${study.case_id}/edit`)}
                          data-testid={`edit-case-study-${study.case_id}`}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(study.case_id)}
                          disabled={deleteLoading === study.case_id}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-case-study-${study.case_id}`}
                        >
                          <Trash2 size={16} className={deleteLoading === study.case_id ? 'animate-pulse' : ''} />
                        </Button>
                      </div>
                    </div>
                    <CardTitle className="text-lg mt-2 line-clamp-2">{study.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Building2 size={16} />
                      <span className="line-clamp-1">{study.industry}</span>
                    </div>
                    {study.applicationType && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Wrench size={16} />
                        <span className="line-clamp-1">{study.applicationType}</span>
                      </div>
                    )}
                    {study.location && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin size={16} />
                        <span>{study.location}</span>
                      </div>
                    )}
                    {study.dateCompleted && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar size={16} />
                        <span>{new Date(study.dateCompleted).toLocaleDateString()}</span>
                      </div>
                    )}
                    
                    {study.problemDescription && (
                      <p className="text-sm text-gray-700 line-clamp-2 mt-2">
                        {study.problemDescription}
                      </p>
                    )}

                    {study.products && study.products.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {study.products.slice(0, 2).map((product, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {product.split(' ').slice(0, 2).join(' ')}
                          </Badge>
                        ))}
                        {study.products.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{study.products.length - 2} more
                          </Badge>
                        )}
                      </div>
                    )}

                    <Button
                      variant="outline"
                      className="w-full mt-4"
                      onClick={() => navigate(`/customer-portal/case-studies/${study.case_id}`)}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CaseStudiesList;
